import React, { useEffect, useState } from 'react';
import { KatLink } from '@amzn/katal-react';
import { LanguageSelector, ModuleDescription, SuButton } from '@amzn/seller-university-ui-components';
import { InstanceUrlMapStateData } from 'src/types/state/learningModule';
import { useBundle } from '@amzn/react-arb-tools';
import HMDFeedback from 'src/components/hmd-feedback';
import { t } from 'src/utils/translation';
import { InstanceLanguage, LANGUAGE_STRING_ID_MAP } from 'src/constants/languageSelector';
import { BUNDLE_NAME, LEARNING_MODULE_STRING_ID } from 'src/constants/localization';
import { useMetrics } from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import { DEFAULT_VIDEO_SRC_PREFERENCE } from 'src/constants/video';
import { useParentEntityContext } from 'src/contexts/ParentEntityContext';
import { getModuleCtaRefTag } from 'src/constants/refTag';
import VideoPlayer from './VideoPlayer';
import type { LearningModuleViewProps } from '../LearningModuleContentView';

export const TEST_ID = {
  VIDEO_ALTERNATIVE_LINK: 'learning-module-video-alternative-link',
  CTA: 'learning-module-video-cta',
};

const VideoView = ({
  learningModule, moduleInstance, handleLanguageSelect, unauthenticated,
  clientId,
}: LearningModuleViewProps) => {
  const [learningModuleBundle] = useBundle(BUNDLE_NAME.LEARNING_MODULE);
  const { instanceMap } = learningModule;
  const { clickMetric } = useMetrics();
  const { parentType, parentId } = useParentEntityContext();
  const ctaRefTag = getModuleCtaRefTag(parentType);

  const handleModuleCtaClick = () => {
    clickMetric({
      feature: FeatureName.ModuleCta,
      metadata: {
        moduleId: learningModule.moduleId,
        moduleTitle: learningModule.title,
        ctaLink: learningModule.ctaLink,
        refTag: ctaRefTag,
        url: window.location.href,
        parentType,
        parentId,
        clientId,
      },
    });
  };

  const handleMoreInfoLinkClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');
    const href = anchor?.getAttribute('href');
    if (anchor && href && learningModule.subContentList?.some((item) => item.link === href)) {
      /* istanbul ignore next -- defensive fallback for null textContent */
      const linkTitle = anchor.textContent ?? '';
      clickMetric({
        feature: FeatureName.ModuleMoreInfoLink,
        metadata: {
          moduleId: learningModule.moduleId,
          linkUrl: href,
          linkTitle,
          url: window.location.href,
        },
      });
    }
  };

  // Handle Video Source
  const availableVideoSources: (keyof InstanceUrlMapStateData)[] = DEFAULT_VIDEO_SRC_PREFERENCE
    .filter((key) => moduleInstance.urlMap[key]);
  const [videoSourceIndex, setVideoSourceIndex] = useState(0);
  const selectedVideoSourceKey = availableVideoSources[videoSourceIndex];
  const selectedVideoUrl = moduleInstance.urlMap[selectedVideoSourceKey];

  const cycleVideoSource = () => {
    setVideoSourceIndex((videoSourceIndex + 1) % availableVideoSources.length);
  };

  // Language Selector Values
  const languageOptions = Object.values(instanceMap).map(({ instanceLanguage }) => ({
    value: instanceLanguage,
    name: LANGUAGE_STRING_ID_MAP[instanceLanguage as InstanceLanguage], // will make a follow-up CR to fix typing on the data model itself
  }));

  // Reset video source index on instance change
  useEffect(() => {
    setVideoSourceIndex(0);
  }, [moduleInstance]);

  return (
    <div className="flex w-full flex-col gap-su-space-05 p-su-space-05">
      {selectedVideoUrl && (
        <div className="flex flex-col gap-su-space-03">
          <VideoPlayer
            videoUrl={selectedVideoUrl}
            learningModuleInstance={moduleInstance}
            clientId={clientId}
          />
        </div>
      )}
      <div className="flex flex-wrap gap-su-space-05 sm_kat_modal:flex-nowrap">
        <ModuleDescription
          className="w-full sm_kat_modal:w-4/5"
          id={learningModule.moduleId}
          content={learningModule.description}
          title={t(learningModuleBundle, LEARNING_MODULE_STRING_ID.MODULE_DESCRIPTION_SUMMARY_TEXT)}
          {...(learningModule.subContentList?.length && !unauthenticated && {
            moreInformationTitle: t(
              learningModuleBundle,
              LEARNING_MODULE_STRING_ID.MORE_INFORMATION_TITLE,
            ),
          })}
          moreInformationItems={
            !unauthenticated ? learningModule.subContentList?.map((subContent) => ({
              link: subContent.link,
              title: subContent.title,
            })) : undefined
          }
          onClick={handleMoreInfoLinkClick}
        />
        <div className="flex w-full flex-col items-end gap-su-space-05 sm_kat_modal:w-auto">
          {availableVideoSources.length > 1 && (
            <KatLink data-testid={TEST_ID.VIDEO_ALTERNATIVE_LINK} href="#" onClick={cycleVideoSource}>
              {t(learningModuleBundle, LEARNING_MODULE_STRING_ID.VIDEO_ALTERNATIVE_LINK)}
            </KatLink>
          )}
          <LanguageSelector
            contentType={t(learningModuleBundle, LEARNING_MODULE_STRING_ID.VIDEO_LANGUAGE_SELECTOR_TEXT)}
            languages={languageOptions}
            selectedLanguage={moduleInstance.instanceLanguage}
            onLanguageChange={handleLanguageSelect}
          />
          {!unauthenticated && (
            <SuButton
              data-testid={TEST_ID.CTA}
              variant="primary"
              label={learningModule.ctaText}
              linkProps={{
                href: learningModule.ctaLink,
                refTag: ctaRefTag,
                onClick: handleModuleCtaClick,
              }}
            />
          )}
          {!unauthenticated && <HMDFeedback instance={moduleInstance} />}
        </div>
      </div>
    </div>
  );
};

export default VideoView;
